/**
 * Finance › Search — one box, then one counterparty's whole story.
 *
 * Answers "what did we pay X, against what invoice, from which card" for any
 * vendor / supplier / third party / payee. Design + full audit trail:
 * docs/superpowers/specs/2026-07-22-vendor-360-search-design.md
 *
 * Two deliberate shape decisions:
 *  - ONE merged timeline, not tabs. Payments in one tab and invoices in another
 *    makes the user do the matching in their head, which is the job.
 *  - Nothing is hidden. Unmatched rows sit at their own date rather than being
 *    filtered out — those are exactly the rows a human needs to act on.
 *
 * This screen writes nothing except an alias from the Unmatched payees tab; every
 * row links out to the screen that already owns the record.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, TextField, InputAdornment, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert,
  Link, Tabs, Tab, Button, Tooltip, Stack, Divider, IconButton, Collapse, Checkbox,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import {
  searchCounterparties, getCounterparty, getCounterpartyEvents, getUnmatchedPayees,
  getUnmatchedPayeeTransactions, mapPayeeToCounterparty,
  getProposals, confirmProposals, rejectProposals,
} from '../../services/financeSearchService';
import {
  journalEntryLink, primaryLinkForEvent, supplierLink,
  bankTxnLink, transactionSearchLink,
} from './financeSearchLinks';

// Money is shown per currency and NEVER blended — a blended total over a table
// with a currency column is a wrong number, not a rounded one.
const money = (amount, currency) =>
  `${currency || ''} ${Number(amount || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`.trim();

// Approximate USD magnitude for the omnibox "Amount" column — a monthly-rate conversion
// used only to rank the list and give a sense of scale, never a reconciled figure. The "~"
// and the tooltip keep it from reading as an exact number.
const approxUsd = (n) =>
  Number(n || 0) > 0 ? `≈ USD ${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : '—';

const KIND_ICON = {
  invoice: <ReceiptLongIcon fontSize="small" color="action" />,
  payment: <PaymentsIcon fontSize="small" color="action" />,
  bank_txn: <CreditCardIcon fontSize="small" color="action" />,
};

// Human bank label for the "via X" note. QB paid_from names (e.g. "Revolut-EUR") are already
// presentable; a raw bank_source ("revolut") is title-cased.
const BANK_PRETTY = { revolut: 'Revolut', amex: 'Amex', opal: 'Opal', wise: 'Wise', paypal: 'PayPal', mercury: 'Mercury' };
const prettyBank = (b) => {
  const s = String(b || '').trim();
  if (!s) return '';
  if (/[-A-Z]/.test(s)) return s;                       // already formatted, e.g. "Revolut-EUR"
  return BANK_PRETTY[s.toLowerCase()] || (s.charAt(0).toUpperCase() + s.slice(1));
};

// Status chip for a timeline row. Only invoices carry a payment status: paid (green), unpaid past
// its due date is overdue (red), otherwise a plain unpaid. Bank/card charges with no invoice carry
// no payment status, so they get no chip — the "Payment · via X" line already says what they are.
const todayIso = () => new Date().toISOString().slice(0, 10);
function statusMeta(ev) {
  if (ev.event_kind !== 'invoice') return null;
  const s = String(ev.status || '').toLowerCase();
  if (s === 'paid') return { label: 'paid', color: 'success' };
  if (s === 'partial' || s === 'partially_paid') return { label: 'partial', color: 'warning' };
  const due = ev.due_date || '';
  if (due && due < todayIso()) return { label: 'overdue', color: 'error' };
  return { label: 'unpaid', color: 'default' };
}

// Every non-invoice timeline row is a money MOVEMENT, and its direction lives in ev.status:
// bank/card rows carry incoming / outgoing / refund / transfer (the amount itself is stored as
// abs()), and customer payments carry 'received'. Return the Reference word + a colour chip so an
// incoming receipt and an outgoing payment are never visually identical — money-in is green, and
// money-out is neutral (Finance bot + Rishabh 2026-07-31: on a wholesale-customer timeline like
// Boohoo every "Payment" was really a receipt). One source of truth so the tab and the CSV export
// stay labelled identically. Returns null for invoices (they keep their paid/unpaid/overdue chip)
// and for any unknown direction (caller falls back to the neutral "Payment").
const FLOW_META = {
  outgoing: { ref: 'Payment', label: 'paid', color: 'default' },
  incoming: { ref: 'Received', label: 'received', color: 'success' },
  received: { ref: 'Received', label: 'received', color: 'success' },
  refund: { ref: 'Refund', label: 'refund', color: 'warning' },
  transfer: { ref: 'Transfer', label: 'transfer', color: 'info' },
};
function flowMeta(ev) {
  if (ev.event_kind === 'invoice') return null;
  return FLOW_META[String(ev.status || '').toLowerCase()] || null;
}

// ---------------------------------------------------------------------------
// CSV export of a counterparty timeline for a date window (Rishabh 2026-07-28: "export
// how much we paid, filtered by period"). The export hits a dedicated read-only endpoint
// that pulls the WHOLE window server-side (not the ~200 rows paged into the table), so a
// period total is complete rather than "as far as I happened to scroll".
// ---------------------------------------------------------------------------
const EXPORT_API = process.env.REACT_APP_API_URL || 'http://localhost:3000';
async function fetchCounterpartyExport(key, { from, to }) {
  const token = localStorage.getItem('token');
  const res = await axios.get(
    `${EXPORT_API}/api/finance/search/counterparty/${encodeURIComponent(key)}/export`,
    { headers: { Authorization: `Bearer ${token}` }, params: { from: from || undefined, to: to || undefined } },
  );
  return res.data;
}

// One CSV cell — quote only when it holds a comma, quote or newline; double any internal quote.
// Also neutralize spreadsheet formula injection: a cell that opens with = + - @ (or a tab/CR) is
// evaluated as a formula by Excel/Sheets, and memo/reference/bank fields come from bank
// descriptions + QB imports, so a leading trigger is prefixed with a quote to force plain text.
// (Amounts are always positive and dates start with a digit, so neither is ever prefixed.)
const csvCell = (v) => {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Human "Type" label for the export. event_kind is 'invoice' | 'bank_txn' | 'payment' — the last
// is a WHOLESALE customer payment (money received FROM them), so it is "Received", never "Payment".
// A bank/card charge carries its direction in `status`, surfaced in the Status column.
const exportType = (ev) =>
  ev.event_kind === 'invoice' ? 'Invoice'
    : ev.event_kind === 'payment' ? 'Received'
      : 'Payment';

// Build the CSV text. Columns mirror the on-screen timeline, plus a machine-friendly numeric
// Amount (no currency symbol) and a separate Currency column so a spreadsheet can pivot/sum
// per currency — a blended cross-currency sum is a wrong number, never a rounded one.
function buildTimelineCsv(events) {
  const header = ['Date', 'Type', 'Reference', 'Status', 'Amount', 'Currency', 'Paid date', 'Paid via', 'Memo'];
  const lines = [header.map(csvCell).join(',')];
  for (const ev of events) {
    const isInvoice = ev.event_kind === 'invoice';
    const fm = flowMeta(ev);
    const ref = isInvoice ? (ev.invoice_number || String(ev.event_id).replace('inv:', '')) : (fm ? fm.ref : 'Payment');
    const st = statusMeta(ev);
    const bank = ev.paid_from ? prettyBank(ev.paid_from) : (ev.bank_source ? prettyBank(ev.bank_source) : '');
    lines.push([
      ev.event_date || '',
      exportType(ev),
      ref,
      st ? st.label : (ev.status || ''),
      Number(ev.amount || 0).toFixed(2),
      ev.currency || '',
      ev.paid_date || '',
      bank,
      ev.memo || '',
    ].map(csvCell).join(','));
  }
  return lines.join('\r\n');
}

// Per-currency CASH movement for the window — the honest headline. Split by direction so it is
// right for both a vendor ("how much we paid") and a wholesale customer ("how much they paid us"),
// and never conflates the two:
//   • Paid out  = bank/card rows with direction 'outgoing' (the bank arm stores amount as abs() and
//     carries direction in `status`; an incoming refund/credit is NOT a payment).
//   • Received  = bank/card 'incoming'/'refund' rows + wholesale customer payments (event_kind
//     'payment' = money received from them).
// Invoices are obligations settled BY a bank row, so they are excluded from BOTH (summing them would
// double count). 'transfer' rows are internal and excluded. Summed in integer CENTS to avoid float
// drift, then divided once for display; never blended across currencies.
function cashMovementByCurrency(events) {
  const out = {}, inc = {};
  for (const ev of events) {
    const c = ev.currency || '';
    const cents = Math.round(Number(ev.amount || 0) * 100);
    if (ev.event_kind === 'bank_txn') {
      const dir = String(ev.status || '').toLowerCase();
      if (dir === 'outgoing') out[c] = (out[c] || 0) + cents;
      else if (dir === 'incoming' || dir === 'refund') inc[c] = (inc[c] || 0) + cents;
    } else if (ev.event_kind === 'payment') {
      inc[c] = (inc[c] || 0) + cents;
    }
  }
  const rows = (m) => Object.entries(m)
    .map(([currency, cents]) => ({ currency, total: cents / 100 }))
    .sort((a, b) => b.total - a.total);
  return { outgoing: rows(out), received: rows(inc) };
}

// Trigger a browser download of a text blob (self-contained; no server round-trip).
function downloadTextFile(filename, text, mime = 'text/csv;charset=utf-8;') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// A fuzzy match must never read as a fact. Exact provenance gets no colour;
// pattern/stem matches are flagged so the reader knows a machine guessed.
// Every method scripts/cp-ledger/phase1_resolver.py can write to
// cp_entry_resolution.method needs an entry here, or MatchBasis falls through to the raw
// snake_case token, uncoloured. Verified 2026-08-03 against the live table: these 11 are
// the complete set (SELECT method, count() FROM cp_entry_resolution GROUP BY method).
// Colour follows the tooltip's promise — success = the entry STATES this counterparty,
// warning = it was inferred from free text or from a last-resort GL lookup.
const BASIS_LABEL = {
  allocation: { label: 'allocation', color: 'success' },
  paid_amount: { label: 'from invoice', color: 'default' },
  invoice: { label: '', color: 'default' },
  account_map: { label: 'GL account', color: 'success' },
  // Pass G: the full account map re-applied to whatever the name-based passes left over.
  // Warning, not success: every name pass has already declined the entry by this point, so
  // the GL account is a last resort rather than a stated counterparty (confidence 0.7).
  account_map_fallback: { label: 'GL account (fallback)', color: 'warning' },
  source_type: { label: 'source type', color: 'success' },
  // Pass H: the JE was posted FROM a supplier invoice / credit note, so the document itself
  // names the supplier — stated, not inferred.
  source_document: { label: 'source document', color: 'success' },
  bank_stem: { label: 'name match', color: 'warning' },
  bank_norm: { label: 'name match', color: 'warning' },
  // The *_desc variants are the same two passes run against the free-text description
  // instead of a named payee.
  bank_stem_desc: { label: 'description match', color: 'warning' },
  bank_norm_desc: { label: 'description match', color: 'warning' },
  bank_desc_pattern: { label: 'description pattern', color: 'warning' },
  merchant_pattern: { label: 'card pattern', color: 'warning' },
  merchant_alias: { label: 'alias match', color: 'warning' },
};

function MatchBasis({ basis, confidence }) {
  const meta = BASIS_LABEL[basis] || { label: basis || '', color: 'default' };
  if (!meta.label) return null;
  const pct = confidence != null ? ` · ${Math.round(Number(confidence) * 100)}%` : '';
  return (
    <Tooltip title={`Resolved by ${basis}${pct}. Warning-coloured matches were inferred, not stated.`}>
      <Chip size="small" variant="outlined" color={meta.color} label={meta.label}
            sx={{ height: 18, fontSize: 11 }} />
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// One unlinked-payee row: expands to show its individual booked transactions,
// each linking to its evidence drawer and journal entry. Answers "these are all
// mapped behind — let me see the transactions".
function UnmatchedRow({ payee, onMap }) {
  const [open, setOpen] = useState(false);
  const [txns, setTxns] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && txns === null) {
      setLoading(true);
      try {
        const res = await getUnmatchedPayeeTransactions(payee.counterparty_name, 200);
        setTxns(res.success ? res.data : []);
      } catch (_) { setTxns([]); }
      finally { setLoading(false); }
    }
  };

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: open ? 'unset' : undefined } }}>
        <TableCell padding="checkbox">
          <IconButton size="small" onClick={toggle} aria-label={open ? 'Hide transactions' : 'Show transactions'}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, cursor: 'pointer' }} onClick={toggle}>
          {payee.counterparty_name}
        </TableCell>
        <TableCell align="right">{Number(payee.txns).toLocaleString()}</TableCell>
        <TableCell align="right">{money(payee.gross, payee.currency)}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>{payee.first_seen} → {payee.last_seen}</TableCell>
        <TableCell align="right">
          <Button size="small" sx={{ textTransform: 'none' }} onClick={() => onMap(payee)}>Map…</Button>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 1 }}>
              {loading && <Box sx={{ py: 1, textAlign: 'center' }}><CircularProgress size={18} /></Box>}
              {!loading && txns && txns.length === 0 && (
                <Typography variant="caption" color="text.secondary">No booked transactions.</Typography>
              )}
              {!loading && txns && txns.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Memo</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {txns.map((t) => (
                      <TableRow key={`${t.bank_source}:${t.bank_transaction_id}`}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.txn_date}</TableCell>
                        <TableCell><Chip size="small" variant="outlined" label={t.bank_source} sx={{ height: 18, fontSize: 11 }} /></TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{t.memo}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                          {money(t.amount, t.currency)}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {t.queue_id && <Link href={bankTxnLink(t.queue_id)} sx={{ mr: 1, fontSize: 12 }}>view</Link>}
                          {journalEntryLink(t.journal_entry_id) && <Link href={journalEntryLink(t.journal_entry_id)} sx={{ fontSize: 12 }}>GL</Link>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <Box sx={{ mt: 0.5 }}>
                <Link href={transactionSearchLink(payee.counterparty_name)} sx={{ fontSize: 12 }}>
                  Open all in Transactions ▸
                </Link>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// ---------------------------------------------------------------------------

// A proposed grouping's reconcile outcome — what Confirm will DO. Mirrors the backend reconcile so
// the reviewer sees the consequence (create a new counterparty vs link into an existing one) BEFORE
// approving — which is what stops a duplicate being minted for a business we already have.
function reconcileChip(rec) {
  if (!rec) return null;
  if (rec.action === 'link') {
    return (
      <Chip size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: 11 }}
            label={`link → ${rec.target_name || rec.target_key}`} />
    );
  }
  if (rec.action === 'ambiguous') {
    return (
      <Tooltip title={`Members already map to multiple counterparties: ${(rec.existing_keys || []).join(', ')}. Confirm will skip this one.`}>
        <Chip size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: 11 }} label="ambiguous — skip" />
      </Tooltip>
    );
  }
  if (rec.action === 'invalid') {
    return <Chip size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} label="invalid name" />;
  }
  return <Chip size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: 11 }} label="create new" />;
}

// "Review groupings" tab — the human gate over LLM canonicalization. The proposer clusters name
// variants of one business; a human confirms each (→ a real counterparty + its aliases) or rejects
// it, so LLM output never becomes shared master-data unreviewed. "Confirmed (audit)" shows what was
// already approved. Materiality (QuickBooks invoice history) lets a reviewer skip the long tail.
function ProposalsPanel() {
  const [status, setStatus] = useState('pending');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [unavailable, setUnavailable] = useState(false);

  const load = useCallback(async (st) => {
    setLoading(true); setError(null); setResult(null); setSelected(new Set());
    try {
      const res = await getProposals({ status: st, limit: 300 });
      if (res.success) { setRows(res.data); setUnavailable(false); }
      else { setRows([]); setUnavailable(res.available === false); }
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message); setRows([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(status); }, [status, load]);

  const isPending = status === 'pending';
  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });
  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  const act = async (kind) => {
    const ids = [...selected];
    if (!ids.length) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const res = kind === 'confirm' ? await confirmProposals(ids) : await rejectProposals(ids);
      if (res.success) {
        setResult(kind === 'confirm'
          ? `Confirmed ${res.confirmed}${res.skipped ? ` · skipped ${res.skipped} (ambiguous)` : ''}.`
          : `Rejected ${res.rejected}.`);
        await load(status);
      } else setError(res.error?.message || 'Action failed');
    } catch (e) {
      setError(e.response?.data?.error?.message || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        The system clusters name variants of the same business (e.g. the eleven Walmart DCs) into one
        proposed counterparty and tags a type. Nothing becomes a real counterparty until a human
        confirms it here. <strong>Confirm</strong> creates the counterparty — or links it into an
        existing one, see the “Will do” column — and starts grouping its activity; <strong>Reject</strong>{' '}
        drops it (e.g. a one-off individual you don’t want tracked). “Total” is its QuickBooks invoice
        history, so you can skip the long tail. “Type” is the model’s classification, shown to help you
        triage — every cluster you confirm is recorded as a customer counterparty.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Button size="small" variant={isPending ? 'contained' : 'outlined'} sx={{ textTransform: 'none' }}
                onClick={() => setStatus('pending')}>Pending review</Button>
        <Button size="small" variant={!isPending ? 'contained' : 'outlined'} sx={{ textTransform: 'none' }}
                onClick={() => setStatus('confirmed')}>Confirmed (audit)</Button>
        <Box sx={{ flexGrow: 1 }} />
        {isPending && (
          <>
            <Button size="small" color="success" variant="outlined" disabled={busy || selected.size === 0}
                    sx={{ textTransform: 'none' }} onClick={() => act('confirm')}>
              Confirm selected ({selected.size})
            </Button>
            <Button size="small" color="warning" variant="outlined" disabled={busy || selected.size === 0}
                    sx={{ textTransform: 'none' }} onClick={() => act('reject')}>
              Reject selected ({selected.size})
            </Button>
          </>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {result && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setResult(null)}>{result}</Alert>}
      {unavailable && (
        <Alert severity="info" sx={{ mb: 1 }}>
          No proposal queue for this company yet — it is populated when the counterparty canonicalization
          proposer runs.
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                {isPending && rows.length > 0 && (
                  <Checkbox size="small" checked={allSelected}
                            indeterminate={selected.size > 0 && !allSelected} onChange={toggleAll} />
                )}
              </TableCell>
              <TableCell>Business</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Names</TableCell>
              <TableCell align="right">Invoices</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Seen</TableCell>
              <TableCell>{isPending ? 'Will do' : 'Result'}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => {
              const m = r.materiality || {};
              return (
                <TableRow key={r.id} hover selected={selected.has(r.id)}>
                  <TableCell padding="checkbox">
                    {isPending && (
                      <Checkbox size="small" checked={selected.has(r.id)} onChange={() => toggle(r.id)} />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {r.canonical_name}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      conf {Math.round(Number(r.confidence || 0))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }}
                          label={String(r.type || '').replace(/_/g, ' ')} />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={(r.members || []).join(' · ') || '—'}>
                      <span style={{ cursor: 'help' }}>
                        {Number(r.member_count || (r.members || []).length)} name{Number(r.member_count) === 1 ? '' : 's'}
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{Number(m.invoices || 0).toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {m.invoices ? money(m.total, m.currency) : '—'}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                    {m.first_invoice ? `${m.first_invoice} → ${m.last_invoice}` : '—'}
                  </TableCell>
                  <TableCell>
                    {isPending ? reconcileChip(r.reconcile)
                      : (r.confirmed_key
                        ? (
                          <Tooltip title={`${r.reviewed_by || ''} ${r.reviewed_at || ''}`.trim() || 'confirmed'}>
                            <Chip size="small" color="info" variant="outlined" sx={{ height: 20, fontSize: 11 }} label={r.confirmed_key} />
                          </Tooltip>
                        )
                        : '—')}
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  {loading ? 'Loading…' : (isPending ? 'No proposals waiting for review.' : 'No confirmed groupings yet.')}
                </Typography>
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

// ---------------------------------------------------------------------------

function SearchView({ onPick }) {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('top'); // 'top' = landing list | 'search'
  const [tab, setTab] = useState(0);
  const [unmatched, setUnmatched] = useState([]);
  // The counterparty ledger is not built for every tenant. The API answers
  // available:false rather than erroring; show that plainly instead of a blank list.
  const [unavailable, setUnavailable] = useState(false);

  // Map-a-payee dialog. This is the ONE write on this screen: it adds a cp_aliases
  // row, which the existing resolver Pass D consumes on its next 05:50 run.
  const [mapping, setMapping] = useState(null);      // the payee row being mapped
  const [mapQuery, setMapQuery] = useState('');
  const [mapOptions, setMapOptions] = useState([]);
  const [mapBusy, setMapBusy] = useState(false);
  const [mapError, setMapError] = useState(null);
  const [mapDone, setMapDone] = useState(null);

  useEffect(() => {
    if (!mapping || mapQuery.trim().length < 2) { setMapOptions([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const res = await searchCounterparties(mapQuery.trim(), { limit: 8 });
        setMapOptions(res.success ? res.data : []);
      } catch (_) { setMapOptions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [mapping, mapQuery]);

  const submitMapping = async (counterpartyKey) => {
    setMapBusy(true); setMapError(null);
    try {
      const res = await mapPayeeToCounterparty(mapping.counterparty_name, counterpartyKey);
      if (res.success) {
        setMapDone(res.data);
        // Drop the mapped payee from the queue so the list reflects the action.
        setUnmatched((prev) => prev.filter((u) => u.counterparty_name !== mapping.counterparty_name));
        setMapping(null); setMapQuery(''); setMapOptions([]);
      } else {
        setMapError(res.error?.message || 'Could not save the mapping');
      }
    } catch (e) {
      // A 409 means the string is already mapped elsewhere — adding a second key
      // would make the resolver treat it as ambiguous and stop resolving it at all.
      setMapError(e.response?.data?.error?.message || e.message);
    } finally { setMapBusy(false); }
  };

  // Empty box => the landing list (top vendors by amount), fetched immediately. A 2+ char
  // query => a debounced name/alias search. One character is too broad, so it clears.
  useEffect(() => {
    const qq = q.trim();
    if (qq.length === 1) { setRows([]); return undefined; }
    const t = setTimeout(async () => {
      setLoading(true); setError(null);
      try {
        const res = await searchCounterparties(qq);
        setRows(res.success ? res.data : []);
        setUnavailable(res.available === false);
        setMode(res.mode || (qq.length === 0 ? 'top' : 'search'));
        if (!res.success) setError(res.error?.message || 'Search failed');
      } catch (e) {
        setError(e.response?.data?.error?.message || e.message);
      } finally { setLoading(false); }
    }, qq.length === 0 ? 0 : 250);
    return () => clearTimeout(t);
  }, [q]);

  // Unmatched-payees tab: its own debounced search (?q=), so a booked-but-unlinked
  // payee (Klaviyo, DHL, a contractor) can be found without scrolling 100 rows.
  const [unmatchedQ, setUnmatchedQ] = useState('');
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  useEffect(() => {
    if (tab !== 1) return undefined;
    const t = setTimeout(async () => {
      setUnmatchedLoading(true);
      try {
        const res = await getUnmatchedPayees({ q: unmatchedQ.trim() || undefined, limit: 100 });
        if (res.success) setUnmatched(res.data);
      } catch (_) { /* surfaced by the empty state */ }
      finally { setUnmatchedLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [tab, unmatchedQ]);

  return (
    <Box>
      <TextField
        fullWidth autoFocus placeholder="Search a vendor, supplier or third party…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setParams(e.target.value ? { q: e.target.value } : {}); }}
        InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>) }}
        sx={{ mb: 1 }}
      />
      <Typography variant="caption" color="text.secondary">
        Matches names and every payee string the resolver knows. Bank and card activity —
        not a guaranteed-complete payment history.
      </Typography>

      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mt: 2, mb: 1 }}>
        <Tab label="Counterparties" sx={{ textTransform: 'none' }} />
        <Tab label="Payees not set up" sx={{ textTransform: 'none' }} />
        <Tab label="Review groupings" sx={{ textTransform: 'none' }} />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {unavailable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The counterparty ledger has not been built for this company yet, so there is nothing to
          search. It is populated by the cp-ledger pipeline.
        </Alert>
      )}

      {tab === 0 && !unavailable && (
        <>
          {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
          {!loading && mode === 'top' && rows.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Top counterparties by all-time amount transacted (suppliers, ad platforms and
              wholesale customers), approximate in USD. Start typing to search every type.
            </Typography>
          )}
          {!loading && q.trim().length >= 2 && rows.length === 0 && !error && (
            <Alert severity="info">
              Nothing matches “{q}”. If money moved under a payee name nobody has mapped yet,
              it will be under <strong>Payees not set up</strong>.
            </Alert>
          )}
          {!loading && rows.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Counterparty</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Activity</TableCell>
                    <TableCell align="right">AP / AR</TableCell>
                    <TableCell>Last seen</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.counterparty_key} hover sx={{ cursor: 'pointer' }}
                              onClick={() => onPick(r.counterparty_key)}>
                      <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={String(r.type || '').replace(/_/g, ' ')} />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                        <Tooltip title="Approximate all-time transacted value, converted to USD at monthly rates. Used to rank; not a reconciled figure.">
                          <span>{approxUsd(r.amount_usd)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">{Number(r.activity_count || 0).toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {/* ap_ar_native = [[currency, ap, ar], ...] in each
                            counterparty's own currency. */}
                        {(r.ap_ar_native || []).length === 0 && '—'}
                        {(r.ap_ar_native || []).map(([ccy, ap, ar, apPo]) => (
                          <React.Fragment key={`${r.counterparty_key}-${ccy}`}>
                            {Number(ap) !== 0 && (
                              <Chip size="small" color="warning" variant="outlined"
                                    label={`owe ${ccy} ${Number(ap).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                                    sx={{ height: 20, fontSize: 11, ml: 0.5 }} />
                            )}
                            {Number(apPo) !== 0 && (
                              <Chip size="small" color="default" variant="outlined"
                                    label={`open PO ${ccy} ${Number(apPo).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                                    sx={{ height: 20, fontSize: 11, ml: 0.5 }} />
                            )}
                            {Number(ar) !== 0 && (
                              <Chip size="small" color="info" variant="outlined"
                                    label={`owed ${ccy} ${Number(ar).toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
                                    sx={{ height: 20, fontSize: 11, ml: 0.5 }} />
                            )}
                          </React.Fragment>
                        ))}
                      </TableCell>
                      <TableCell>{r.last_activity || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && !unavailable && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            We can name these from the bank/card feed (e.g. Klaviyo, booked to Software &amp;
            Subscriptions), but they aren&apos;t set up as a counterparty — so their transactions
            (and their name variants, like the four spellings of Klaviyo) aren&apos;t grouped under
            one record with a running total. Expand a row to see its transactions;{' '}
            <strong>Map…</strong> sets it up as a counterparty. Biggest first; PayPal excluded
            (its payee column holds retail customers).
          </Typography>
          <TextField
            fullWidth size="small" sx={{ mb: 1 }}
            placeholder="Search payees not set up…"
            value={unmatchedQ}
            onChange={(e) => setUnmatchedQ(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Payee string</TableCell>
                  <TableCell align="right">Txns</TableCell>
                  <TableCell align="right">Gross</TableCell>
                  <TableCell>Seen</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unmatched.map((u) => (
                  <UnmatchedRow key={u.counterparty_name} payee={u}
                                onMap={(p) => { setMapping(p); setMapQuery(''); setMapError(null); }} />
                ))}
                {unmatched.length === 0 && (
                  <TableRow><TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      {unmatchedLoading ? 'Loading…' : (unmatchedQ ? `No payee matches “${unmatchedQ}”.` : 'Every payee is set up as a counterparty.')}
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {mapDone && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setMapDone(null)}>
              “{mapDone.normalized_string}” → <strong>{mapDone.counterparty_key}</strong>. {mapDone.effective}
            </Alert>
          )}

          <Dialog open={Boolean(mapping)} onClose={() => setMapping(null)} fullWidth maxWidth="sm">
            <DialogTitle>Map a payee to a counterparty</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Every transaction whose payee reads{' '}
                <Box component="code" sx={{ fontFamily: 'monospace' }}>{mapping?.counterparty_name}</Box>{' '}
                will resolve to the counterparty you pick. This adds an alias the resolver reads —
                it does not move any money or touch the ledger.
              </Typography>
              {mapError && <Alert severity="error" sx={{ my: 1 }}>{mapError}</Alert>}
              <TextField
                fullWidth autoFocus size="small" sx={{ mt: 1 }}
                label="Find the counterparty" value={mapQuery}
                onChange={(e) => setMapQuery(e.target.value)}
              />
              <Box sx={{ mt: 1 }}>
                {mapOptions.map((o) => (
                  <Button key={o.counterparty_key} fullWidth disabled={mapBusy}
                          sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                          onClick={() => submitMapping(o.counterparty_key)}>
                    {o.name}
                    <Chip size="small" variant="outlined" sx={{ ml: 1, height: 18, fontSize: 11 }}
                          label={String(o.type || '').replace(/_/g, ' ')} />
                  </Button>
                ))}
                {mapQuery.trim().length >= 2 && mapOptions.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                    No counterparty matches “{mapQuery}”.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMapping(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* ProposalsPanel probes the proposals table itself (separate from the counterparty-search
          `unavailable` flag), so it is not gated on it — it shows its own availability message. */}
      {tab === 2 && <ProposalsPanel />}
    </Box>
  );
}

// ---------------------------------------------------------------------------

function CounterpartyView({ counterpartyKey, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [extra, setExtra] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [timelineQ, setTimelineQ] = useState('');
  // CSV export dialog (period picker). Kept as component state so all export hooks sit above
  // the loading/error/!data early returns (Rules of Hooks).
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState(null);
  const [exportSummary, setExportSummary] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setExtra([]);
      try {
        const res = await getCounterparty(counterpartyKey);
        if (cancelled) return;
        if (res.success) setData(res.data);
        else setError(res.error?.message || 'Failed to load');
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error?.message || e.message);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [counterpartyKey]);

  const events = useMemo(() => [...(data?.events || []), ...extra], [data, extra]);

  const loadMore = useCallback(async () => {
    if (!events.length) return;
    setLoadingMore(true);
    try {
      // Full composite cursor from the last row — date alone would skip rows that
      // share the boundary date.
      const last = events[events.length - 1];
      const res = await getCounterpartyEvents(counterpartyKey, {
        before: last.event_date, beforeKind: last.event_kind, beforeId: last.event_id, limit: 200,
      });
      if (res.success) setExtra((prev) => [...prev, ...res.data]);
    } catch (_) { /* keep what is already shown */ }
    finally { setLoadingMore(false); }
  }, [counterpartyKey, events]);

  // INVOICE-CENTRIC timeline (Rishabh 2026-07-27): every supplier invoice is its own row and always
  // shows its status. A bank/card payment that settled one or more invoices folds INTO those invoice
  // rows — its paying bank surfaces as "via X" on the invoice — so it does NOT get a duplicate row.
  // Only a bank/card charge with NO invoice match (e.g. a SaaS card charge) keeps its own "Payment"
  // row. This ends the "same invoice appears as INV# here and 'To Vendor' there" inconsistency.
  // NOTE: these useMemo hooks MUST sit above the loading/error/!data early returns — a hook after a
  // conditional return changes the hook count between renders and crashes the component.
  //
  // A payment only folds away when EVERY invoice it settled is actually on this loaded page. A
  // payment whose settled invoice is off-page (later keyset page) or that the matcher never linked
  // therefore keeps its own "Payment" row rather than silently vanishing. The fold map keys on
  // matched_invoices[].id, the same id space as the invoice event_id suffix (`inv:<id>`); if a
  // payment isn't folded, the "via X" note simply falls back to the invoice's own ev.paid_from.
  const presentInvoiceIds = useMemo(() => {
    const s = new Set();
    for (const e of events) if (e.event_kind === 'invoice') s.add(String(e.event_id).replace('inv:', ''));
    return s;
  }, [events]);
  const isFoldablePayment = (e) => {
    if (e.event_kind !== 'bank_txn') return false;
    const mis = e.matched_invoices || [];
    return mis.length > 0 && mis.every((mi) => presentInvoiceIds.has(String(mi.id)));
  };
  const foldedBankByInvoice = useMemo(() => {
    const m = {};   // invoiceId -> ordered list of DISTINCT paying banks (usually exactly one)
    for (const e of events) {
      if (!isFoldablePayment(e)) continue;
      const b = e.bank_source || '';
      if (!b) continue;
      for (const mi of e.matched_invoices) {
        const id = String(mi.id);
        if (!m[id]) m[id] = [];
        // An invoice settled across two different banks (split/corrected payment) lists both,
        // rather than silently showing only the first — a specific-but-incomplete "via X" is worse
        // than the full list.
        if (!m[id].includes(b)) m[id].push(b);
      }
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, presentInvoiceIds]);
  const visibleEvents = useMemo(
    () => events.filter((e) => !isFoldablePayment(e)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events, presentInvoiceIds],
  );

  // Timeline filter (Rishabh 2026-07-27): a busy counterparty can have 100+ rows (Google Ireland =
  // 149), so let the user type to find a specific invoice/payment. Filters the ALREADY-LOADED rows
  // client-side across every visible field — invoice #, date, paid date, amount (raw + formatted),
  // currency, paying bank, and status — so "5593" jumps to that invoice and "revolut" narrows to
  // Revolut payments. Must sit above the loading/error/!data early returns (Rules of Hooks).
  const filteredEvents = useMemo(() => {
    const q = timelineQ.trim().toLowerCase();
    if (!q) return visibleEvents;
    return visibleEvents.filter((ev) => {
      const isInvoice = ev.event_kind === 'invoice';
      const invId = isInvoice ? String(ev.event_id).replace('inv:', '') : '';
      const foldedBanks = isInvoice ? (foldedBankByInvoice[invId] || []) : [];
      const bankLabel = ev.paid_from
        ? prettyBank(ev.paid_from)
        : foldedBanks.length
          ? foldedBanks.map(prettyBank).join(', ')
          : (!isInvoice && ev.bank_source ? prettyBank(ev.bank_source) : '');
      const st = statusMeta(ev);
      const hay = [
        isInvoice ? (ev.invoice_number || ev.event_id) : 'payment',
        ev.invoice_number,
        ev.event_date,
        ev.paid_date,
        String(ev.amount ?? ''),          // raw digits so "20931" matches "20,931.42"
        money(ev.amount, ev.currency),    // formatted so "usd 20,931" matches too
        ev.currency,
        bankLabel,
        st ? st.label : '',
        ev.event_kind,
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleEvents, foldedBankByInvoice, timelineQ]);

  if (loading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  const cp = data.counterparty;
  const sid = data.supplier_id;

  // Fetch the whole window server-side, build the CSV, download it, and keep a per-currency
  // "paid" sanity figure to show in the dialog. Plain function (not a hook) — it only runs on
  // click, by which point `data`/`cp` are loaded. from/to blank = all dates.
  const runExport = async () => {
    if (exportFrom && exportTo && exportFrom > exportTo) {
      setExportErr('"From" is after "To" — swap the dates.');
      return;
    }
    setExporting(true); setExportErr(null); setExportSummary(null);
    try {
      const res = await fetchCounterpartyExport(counterpartyKey, { from: exportFrom, to: exportTo });
      if (!res || !res.success) { setExportErr(res?.error?.message || 'Export failed'); return; }
      if (res.available === false) { setExportErr(res.note || 'The counterparty ledger is not available yet.'); return; }
      const rows = res.data || [];
      if (rows.length === 0) { setExportErr('No invoices or payments in that date range.'); return; }
      const csv = buildTimelineCsv(rows);
      const safeName = String(cp.name || 'counterparty').replace(/[^\w.-]+/g, '_').slice(0, 60);
      const range = `${exportFrom || 'all'}_to_${exportTo || 'all'}`;
      downloadTextFile(`${safeName}_timeline_${range}.csv`, csv);
      setExportSummary({ count: rows.length, truncated: !!res.truncated, cash: cashMovementByCurrency(rows) });
    } catch (e) {
      setExportErr(e.response?.data?.error?.message || e.message || 'Export failed');
    } finally { setExporting(false); }
  };

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2, textTransform: 'none' }}>
        Back to search
      </Button>

      {/* Header */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h5" fontWeight={600}>{cp.name}</Typography>
          <Chip size="small" variant="outlined" label={String(cp.type || '').replace(/_/g, ' ')} />
          {sid > 0 && (
            <Link href={supplierLink(sid)} underline="hover" sx={{ fontSize: 13 }}>
              Supplier record <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
            </Link>
          )}
        </Stack>

        {/* AP/AR from cp_ledger, per NATIVE currency (its own axis, never blended into
            the invoice totals). AP = we owe them; AR = they owe us. One chip per
            currency, so a multi-currency counterparty is never cross-summed. */}
        {(data.ap_ar_by_currency || []).length > 0 && (
          <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
            {(data.ap_ar_by_currency || []).map((b) => (
              <React.Fragment key={`apar-${b.currency}`}>
                {Number(b.ap) !== 0 && (
                  <Chip color="warning" variant="outlined"
                        label={`We owe: ${money(b.ap, b.currency)}`} />
                )}
                {Number(b.ap_po) !== 0 && (
                  <Tooltip title="Open purchase-order commitment — goods ordered but not yet received/invoiced. A PO is a commitment, not a payable, so it is shown separately from what we owe.">
                    <Chip color="default" variant="outlined"
                          label={`Open PO commitment: ${money(b.ap_po, b.currency)}`} />
                  </Tooltip>
                )}
                {Number(b.ar) !== 0 && (
                  <Chip color={Number(b.ar) > 0 ? 'info' : 'default'} variant="outlined"
                        label={`They owe us: ${money(b.ar, b.currency)}`} />
                )}
              </React.Fragment>
            ))}
          </Stack>
        )}

        {/* Clean labelled stats (Rishabh 2026-07-24: the old Invoiced / Bank&card chips were not
            intuitive). Lead with what we OWE, then paid, then the invoiced total; bank/card money
            is a clearly-labelled secondary figure (it's the subset settled through the bank feed). */}
        <Stack direction="row" spacing={5} flexWrap="wrap" sx={{ mt: 2 }}>
          {(data.totals_by_currency || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No invoices</Typography>
          ) : data.totals_by_currency.map((t) => {
            const invoiced = Number(t.paid || 0) + Number(t.outstanding || 0);
            const isOwed = Number(t.outstanding) > 0.005;
            return (
              <React.Fragment key={`tot-${t.currency}`}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Outstanding</Typography>
                  <Typography variant="h6" fontWeight={700} color={isOwed ? 'warning.main' : 'success.main'}>
                    {isOwed ? money(t.outstanding, t.currency) : 'Nil'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Paid</Typography>
                  <Typography variant="h6" fontWeight={600}>{money(t.paid, t.currency)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total invoiced</Typography>
                  <Typography variant="h6" fontWeight={600}>{money(invoiced, t.currency)}</Typography>
                </Box>
              </React.Fragment>
            );
          })}
        </Stack>

        {(data.aliases || []).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">Known as</Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
              {data.aliases.map((a) => (
                <Chip key={a.normalized_string} size="small" label={a.normalized_string}
                      sx={{ fontFamily: 'monospace', fontSize: 11 }} />
              ))}
            </Box>
          </>
        )}
      </Paper>

      {/* Timeline */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ mb: 1, rowGap: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Timeline ({timelineQ.trim()
            ? `${filteredEvents.length} of ${visibleEvents.length}`
            : `${visibleEvents.length}${events.length >= 200 ? '+' : ''}`})
        </Typography>
        {visibleEvents.length > 0 && (
          <>
            <TextField
              size="small"
              placeholder="Filter by invoice #, amount, bank, date…"
              value={timelineQ}
              onChange={(e) => setTimelineQ(e.target.value)}
              InputProps={{
                startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
                endAdornment: timelineQ ? (
                  <IconButton size="small" aria-label="clear timeline filter" onClick={() => setTimelineQ('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
              sx={{ ml: { sm: 'auto' }, minWidth: { xs: '100%', sm: 320 } }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={() => { setExportErr(null); setExportSummary(null); setExportOpen(true); }}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Export CSV
            </Button>
          </>
        )}
      </Stack>
      {visibleEvents.length === 0 ? (
        <Alert severity="info">
          Nothing recorded against this counterparty yet. If money moved under a payee string
          nobody has mapped, it will be under <strong>Unmatched payees</strong> on the search screen.
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={36} />
                <TableCell>Date</TableCell>
                <TableCell>Reference</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Open</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEvents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No rows match your search.{events.length >= 200 ? ' It may be on an older page. Try Load older below.' : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredEvents.map((ev) => {
                const isInvoice = ev.event_kind === 'invoice';
                const link = primaryLinkForEvent(ev, sid);
                const invId = isInvoice ? String(ev.event_id).replace('inv:', '') : '';
                // Paying bank label: prefer the QB bill-payment account (ev.paid_from, e.g.
                // "Revolut-EUR"); else the bank(s) of the payment(s) folded into this invoice; else
                // the charge's own bank_source (for an invoice-less "Payment" row). Already pretty.
                const foldedBanks = isInvoice ? (foldedBankByInvoice[invId] || []) : [];
                const bankLabel = ev.paid_from
                  ? prettyBank(ev.paid_from)
                  : foldedBanks.length
                    ? foldedBanks.map(prettyBank).join(', ')
                    : (!isInvoice && ev.bank_source ? prettyBank(ev.bank_source) : '');
                const st = statusMeta(ev);
                // Reference reads consistently by TYPE: an invoice shows its number; a money
                // movement (bank/card charge OR customer payment) shows what the money did
                // (Payment/Received/Refund/Transfer) via flowMeta; anything else falls back to
                // "Payment" (never the redundant "To <vendor>" — you are already on that page).
                const fm = flowMeta(ev);
                const refLabel = isInvoice ? (ev.invoice_number || ev.event_id)
                  : (fm ? fm.ref : 'Payment');
                return (
                  <TableRow key={ev.event_id} hover>
                    <TableCell>{KIND_ICON[ev.event_kind]}</TableCell>
                    {/* One date — the invoice date (or the charge date). The paid date lives in the
                        sub-line below the reference so it never falls out of chronological order. */}
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2">{ev.event_date}</Typography>
                    </TableCell>
                    <TableCell>
                      {link ? (
                        <Link href={link} sx={{ fontWeight: 500, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                          {refLabel}
                        </Link>
                      ) : (
                        <Typography variant="body2" component="span">{refLabel}</Typography>
                      )}
                      {st && (
                        <Chip size="small" variant="outlined" color={st.color} label={st.label}
                              sx={{ ml: 0.5, height: 18, fontSize: 11 }} />
                      )}
                      {fm && (
                        <Chip size="small" variant="outlined" color={fm.color} label={fm.label}
                              sx={{ ml: 0.5, height: 18, fontSize: 11 }} />
                      )}
                      {/* Paid / via-bank sub-line — folds the settling payment's story onto the
                          invoice (paid when, via which bank), so payments need no separate row. */}
                      {isInvoice && ev.paid_date ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          paid {ev.paid_date}{bankLabel ? ` · via ${bankLabel}` : ''}
                        </Typography>
                      ) : (!isInvoice && bankLabel) ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          via {bankLabel}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {money(ev.amount, ev.currency)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {link && <Link href={link} sx={{ fontSize: 12 }}>Open</Link>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {events.length >= 200 && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button onClick={loadMore} disabled={loadingMore} sx={{ textTransform: 'none' }}>
            {loadingMore ? 'Loading…' : 'Load older'}
          </Button>
        </Box>
      )}

      {data.coverage_note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          {data.coverage_note}
        </Typography>
      )}

      {/* CSV export — pick a period, pull the whole window from the server, download. */}
      <Dialog open={exportOpen} onClose={() => !exporting && setExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Export timeline to CSV</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Every invoice and payment for <strong>{cp.name}</strong> in the chosen period. Leave a date
            blank for no bound. Pulls the whole period from the server, not just the rows on screen.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              type="date" label="From" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={exportFrom}
              onChange={(e) => { setExportFrom(e.target.value); setExportSummary(null); setExportErr(null); }}
            />
            <TextField
              type="date" label="To" size="small" fullWidth
              InputLabelProps={{ shrink: true }}
              value={exportTo}
              onChange={(e) => { setExportTo(e.target.value); setExportSummary(null); setExportErr(null); }}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            The file lists invoices and payments as separate rows. For "how much we paid", use the
            totals below (or filter to Type = Payment) — don't sum the whole Amount column, or settled
            invoices double-count against their payments.
          </Typography>
          {exportErr && <Alert severity="warning" sx={{ mt: 2 }}>{exportErr}</Alert>}
          {exportSummary && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Downloaded {exportSummary.count} row{exportSummary.count === 1 ? '' : 's'}.
              {exportSummary.cash.outgoing.length > 0 && (
                <> Paid out: {exportSummary.cash.outgoing.map((p) => money(p.total, p.currency)).join(', ')}.</>
              )}
              {exportSummary.cash.received.length > 0 && (
                <> Received: {exportSummary.cash.received.map((p) => money(p.total, p.currency)).join(', ')}.</>
              )}
              {exportSummary.cash.outgoing.length === 0 && exportSummary.cash.received.length === 0 &&
                ' No bank/card cash movement in this window (invoices only).'}
              {exportSummary.truncated && ' Capped at 5,000 rows — narrow the period for the rest.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)} disabled={exporting} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Button
            onClick={runExport}
            disabled={exporting}
            variant="contained"
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
            sx={{ textTransform: 'none' }}
          >
            {exporting ? 'Preparing…' : 'Download CSV'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ---------------------------------------------------------------------------

export default function FinanceSearch() {
  const navigate = useNavigate();
  const { counterpartyKey } = useParams();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <Typography variant="h4" fontWeight={600} gutterBottom>Search</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Every payment, invoice and card charge for one counterparty, in one timeline.
      </Typography>

      {counterpartyKey
        ? <CounterpartyView counterpartyKey={decodeURIComponent(counterpartyKey)}
                            onBack={() => navigate('/finance/search')} />
        : <SearchView onPick={(k) => navigate(`/finance/counterparty/${encodeURIComponent(k)}`)} />}
    </Container>
  );
}
